import {
  FeedbackDarkFishIcon,
  FeedbackGrayFishIcon,
  FeedbackGrayStarIcon,
  FeedbackGreenFishIcon,
  FeedbackRedStarIcon,
} from '@/utils/helpers/icons.helper';
import { isMobile } from '@/utils/helpers/ui.helper';
import { t } from 'i18next';
import { default as React, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

const StyledStarRating = styled.div<{ $disabled: boolean }>`
  display: flex;
  gap: 0;
  align-items: center;

  .star {
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    border-radius: 4px;
    padding: 2px;
  }

  &.mobile .star {
    svg {
      width: 48px;
      height: 48px;
    }
  }
`;

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  maxStars?: number;
  disabled?: boolean;
  icon?: 'star' | 'fish';
  greyedOut?: boolean;
}

export const StarRating: React.FC<StarRatingProps> = ({
  value,
  onChange,
  maxStars = 5,
  disabled = false,
  icon = 'star',
  greyedOut = false,
}: StarRatingProps) => {
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const starRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mobile = isMobile();

  const handleStarHover = useCallback(
    (starIndex: number) => {
      if (!disabled && !mobile) {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
        setHoveredRating(starIndex);
      }
    },
    [disabled, mobile],
  );

  const handleStarLeave = useCallback(() => {
    if (!disabled && !mobile) {
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredRating(0);
      }, 50);
    }
  }, [disabled, mobile]);

  const handleStarClick = (starIndex: number) => {
    if (!disabled && onChange) {
      onChange(starIndex);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, starIndex: number) => {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowRight': {
        event.preventDefault();
        const nextStar = Math.min(starIndex + 1, maxStars);
        starRefs.current[nextStar - 1]?.focus();
        break;
      }
      case 'ArrowLeft': {
        event.preventDefault();
        const prevStar = Math.max(starIndex - 1, 1);
        starRefs.current[prevStar - 1]?.focus();
        break;
      }
      case 'Enter':
        event.preventDefault();
        handleStarClick(starIndex);
        break;
    }
  };

  const renderIcon = (isFilled: boolean) => {
    if (icon === 'fish') {
      if (greyedOut) {
        return isFilled ? <FeedbackDarkFishIcon /> : <FeedbackGrayFishIcon />;
      }
      return isFilled ? <FeedbackGreenFishIcon /> : <FeedbackGrayFishIcon />;
    }
    return isFilled ? <FeedbackRedStarIcon /> : <FeedbackGrayStarIcon />;
  };

  const renderStars = () => {
    const stars = [];
    const currentValue = !mobile && hoveredRating > 0 ? hoveredRating : value;

    for (let i = 1; i <= maxStars; i++) {
      stars.push(
        <span
          key={i}
          ref={(el) => (starRefs.current[i - 1] = el)}
          className="star"
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-label={
            i === 1 ? t('generic.one.star') : t('generic.num.stars', { num: i })
          }
          onMouseEnter={() => handleStarHover(i)}
          onMouseLeave={handleStarLeave}
          onClick={() => handleStarClick(i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
        >
          {renderIcon(i <= currentValue)}
        </span>,
      );
    }

    return stars;
  };

  return (
    <StyledStarRating $disabled={disabled} className={mobile ? 'mobile' : ''}>
      {renderStars()}
    </StyledStarRating>
  );
};
